import { useEffect, useState } from "react";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const FLAGS = { CH: "🇨🇭", DE: "🇩🇪", AT: "🇦🇹" };
const COUNTRIES = ["CH", "DE", "AT"];
const LANGS = ["DE", "EN", "FR", "IT"];

const STATUS_BADGES = {
    active: { label: "Aktiv", className: "bg-emerald-100 text-emerald-700 border-0" },
    unsubscribed: { label: "Abgemeldet", className: "bg-slate-200 text-slate-600 border-0" },
    bounced: { label: "Bounced", className: "bg-rose-100 text-rose-700 border-0" },
    complained: { label: "Beschwerde", className: "bg-amber-100 text-amber-700 border-0" },
};

const empty = { agency_name: "", contact_person: "", email: "", country: "CH", language: "DE", city: "", notes: "" };

export default function ContactsPage() {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filterCountry, setFilterCountry] = useState("all");
    const [filterLang, setFilterLang] = useState("all");
    const [onlyUncontacted, setOnlyUncontacted] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (search) params.search = search;
        if (filterCountry !== "all") params.country = filterCountry;
        if (filterLang !== "all") params.language = filterLang;
        if (onlyUncontacted) params.only_uncontacted = true;
        try {
            const { data } = await api.get("/contacts", { params });
            setContacts(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [filterCountry, filterLang, onlyUncontacted]);
    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [search]);

    const openCreate = () => { setEditingId(null); setForm(empty); setDialogOpen(true); };
    const openEdit = (c) => { setEditingId(c.id); setForm({ ...c }); setDialogOpen(true); };

    const submit = async () => {
        try {
            // Convert empty salutation string to null (so backend Optional[Salutation] accepts it)
            const payload = { ...form, salutation: form.salutation || null };
            if (editingId) {
                await api.put(`/contacts/${editingId}`, payload);
                toast.success("Kontakt aktualisiert");
            } else {
                await api.post("/contacts", payload);
                toast.success("Kontakt hinzugefügt");
            }
            setDialogOpen(false); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Kontakt wirklich löschen?")) return;
        try {
            await api.delete(`/contacts/${id}`);
            toast.success("Kontakt gelöscht");
            load();
        } catch { toast.error("Löschen fehlgeschlagen"); }
    };

    return (
        <div className="space-y-8 animate-fade-in-up" data-testid="contacts-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#007bff] font-bold mb-2">Verwaltung</div>
                    <h1 className="font-display text-5xl text-[#1a2b48] leading-none">Kontakte.</h1>
                    <p className="text-slate-500 mt-3 max-w-xl">Reisebüros &amp; Travel-Agenturen aus DE / AT / CH manuell verwalten.</p>
                </div>
                <Button data-testid="add-contact-btn" onClick={openCreate}
                    className="bg-[#007bff] hover:bg-[#0056b3] h-11 px-5 shadow-md shadow-blue-500/20">
                    <Plus className="w-4 h-4 mr-2" /> Reisebüro hinzufügen
                </Button>
            </div>

            {/* Filters */}
            <Card className="p-4 bg-white border-slate-200 rounded-xl">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[240px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input data-testid="contacts-search"
                            placeholder="Suchen nach Name, E-Mail, Stadt ..."
                            value={search} onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 h-10 bg-slate-50 border-slate-200" />
                    </div>
                    <Select value={filterCountry} onValueChange={setFilterCountry}>
                        <SelectTrigger className="w-[160px] h-10" data-testid="filter-country"><SelectValue placeholder="Land" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Alle Länder</SelectItem>
                            {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{FLAGS[c]} {c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filterLang} onValueChange={setFilterLang}>
                        <SelectTrigger className="w-[160px] h-10" data-testid="filter-language"><SelectValue placeholder="Sprache" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Alle Sprachen</SelectItem>
                            {LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </Card>

            {/* List */}
            <Card className="bg-white border-slate-200 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-slate-400 text-sm">Lädt ...</div>
                ) : contacts.length === 0 ? (
                    <div className="p-16 text-center">
                        <UsersIcon className="w-12 h-12 text-slate-200 mx-auto mb-4" strokeWidth={1.3} />
                        <h3 className="font-display text-2xl text-[#1a2b48]">Noch keine Kontakte.</h3>
                        <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
                            Fügen Sie Ihre ersten Reisebüros aus Deutschland, Österreich oder der Schweiz hinzu.
                        </p>
                        <Button onClick={openCreate} className="mt-6 bg-[#007bff] hover:bg-[#0056b3]">
                            <Plus className="w-4 h-4 mr-2" /> Ersten Kontakt anlegen
                        </Button>
                    </div>
                ) : (
                    <table className="w-full" data-testid="contacts-table">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                <th className="text-left py-3 px-5">Reisebüro</th>
                                <th className="text-left py-3 px-5">E-Mail</th>
                                <th className="text-left py-3 px-5">Land</th>
                                <th className="text-left py-3 px-5">Sprache</th>
                                <th className="text-left py-3 px-5">Status</th>
                                <th className="text-right py-3 px-5">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {contacts.map((c) => (
                                <tr key={c.id} className="hover:bg-slate-50/40 transition-colors" data-testid={`contact-row-${c.id}`}>
                                    <td className="py-4 px-5">
                                        <div className="font-medium text-[#1a2b48] flex items-center gap-2">
                                            {c.agency_name}
                                            {c.last_emailed_at && (
                                                <span title={`Bereits ${c.email_count}× angeschrieben — letzte: ${new Date(c.last_emailed_at).toLocaleDateString("de-DE")}`}
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-[10px] font-semibold text-[#007bff]">
                                                    ✓ {c.email_count}×
                                                </span>
                                            )}
                                        </div>
                                        {(c.contact_person || c.salutation) && (
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                {c.salutation ? `${c.salutation} ` : ""}{c.contact_person}{c.city ? ` · ${c.city}` : ""}
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-4 px-5 text-sm text-slate-600">{c.email}</td>
                                    <td className="py-4 px-5"><span className="text-base">{FLAGS[c.country]}</span> <span className="ml-1 text-sm font-medium text-slate-700">{c.country}</span></td>
                                    <td className="py-4 px-5"><Badge variant="secondary" className="bg-blue-50 text-[#007bff] border-0 font-semibold">{c.language}</Badge></td>
                                    <td className="py-4 px-5"><Badge className={STATUS_BADGES[c.status]?.className}>{STATUS_BADGES[c.status]?.label || c.status}</Badge></td>
                                    <td className="py-4 px-5">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`} className="h-8 w-8 text-slate-500 hover:text-[#007bff]"><Pencil className="w-4 h-4" /></Button>
                                            <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`delete-contact-${c.id}`} className="h-8 w-8 text-slate-500 hover:text-rose-500"><Trash2 className="w-4 h-4" /></Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg" data-testid="contact-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-[#1a2b48]">
                            {editingId ? "Kontakt bearbeiten" : "Neues Reisebüro"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                        <div className="md:col-span-2 space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Name des Reisebüros *</Label>
                            <Input data-testid="contact-name-input" value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })} placeholder="z.B. Alpine Travel GmbH" />
                        </div>
                        <div className="md:col-span-2 space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">E-Mail *</Label>
                            <Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="kontakt@reisebuero.ch" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Anrede</Label>
                            <Select value={form.salutation || "none"} onValueChange={(v) => setForm({ ...form, salutation: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="contact-salutation-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— (keine)</SelectItem>
                                    <SelectItem value="Herr">Herr</SelectItem>
                                    <SelectItem value="Frau">Frau</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Ansprechpartner (Vor- + Nachname)</Label>
                            <Input data-testid="contact-person-input" value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} placeholder="Maria Müller" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Stadt</Label>
                            <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Zürich" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Land *</Label>
                            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                                <SelectTrigger data-testid="contact-country-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{FLAGS[c]} {c}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Sprache *</Label>
                            <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                                <SelectTrigger data-testid="contact-language-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="md:col-span-2 space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Notizen</Label>
                            <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                        <Button onClick={submit} data-testid="contact-save-btn" className="bg-[#007bff] hover:bg-[#0056b3]">{editingId ? "Speichern" : "Hinzufügen"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
